<script setup>
import {plainStripeError} from '~/utils/stripeErrors.js';
// Interactive entry-fee payment. Saved-card first: the member's platform card
// (shared with meets — added via AddCardDialog, managed on /your-profile/payments)
// pays in one click through the server-side charge pipeline. "Other payment
// methods" falls back to the Stripe Payment Element (wallets + card, no saving):
//   POST entryFeeObligations/{id}/payWithSavedMethod   ← one-click path
//   POST entryFeeObligations/{id}/checkout → mount Element → confirmPayment
// Both settle the obligation; emits 'paid' so the parent can refresh.
import TlDialog from "~/components/TlDialog.vue";
import AddCardDialog from "~/components/payments/AddCardDialog.vue";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {useStripePayments} from "~/composables/useStripePayments";
import {usePaymentsDisplay} from "~/composables/usePaymentsDisplay";
import {endpoints} from "~/network/endpoints.js";
import {computed, nextTick, ref, watch} from "vue";
import {useMeetFormat} from "~/composables/useMeetFormat";
import {
  ENTRY_FEE_PAY_TICK,
  ENTRY_FEE_TERMS_ALREADY_ACCEPTED,
  entryFeePayBlocked,
  entryFeePayBody,
  entryFeePayNeedsTick,
  payCancellationLine,
  payeeLine,
} from '~/composables/usePayingHonestly.js';

const props = defineProps({
  // { id, amountMinor, currency, entityName }
  obligation: {type: Object, default: null},
});
const open = defineModel({type: Boolean, default: false});
const emit = defineEmits(['paid']);

const {httpRequest} = useHttpRequest();
const {mountPaymentElement, confirmPayment} = useStripePayments();
const {normalizeErrors, money, cardLabel, isCardExpired} = usePaymentsDisplay();
const {termsAsHtml} = useMeetFormat();
// CCRs reg 13: the club is the seller, named with its address.
const payee = computed(() => payeeLine(props.obligation?.clubName, props.obligation?.clubAddress));

// CCR 2013 reg 36: the entry-fee terms in force are shown and accepted BEFORE
// any payment starts. The API refuses both payment paths without acceptTerms,
// unless this member already accepted these terms (Join and pay, 28 Sep 2026:
// the obligation says so with termsAcceptedByMe). Then there is no tick, the
// terms stay one tap away, and acceptTerms is not sent.
const termsAccepted = ref(false);
const needsTick = computed(() => entryFeePayNeedsTick(props.obligation));
const blocked = computed(() => entryFeePayBlocked(props.obligation, termsAccepted.value));
const showAcceptedTerms = ref(false);
// CCRs reg 13 / Sch 2: the cancellation position, as the API wrote it from the
// competition's dates. Directly above the tick; above the accepted line when
// there is no tick. Nothing from an older API.
const cancellationLine = computed(() => payCancellationLine(props.obligation));
const tickAsked = computed(() => !!props.obligation?.termsHtml && needsTick.value);

// ── saved cards ──
const cards = ref(null); // null = loading
const addCardOpen = ref(false);
const loadCards = async () => {
  try {
    cards.value = await httpRequest(endpoints.payments.myPaymentMethods, null);
  } catch {
    cards.value = [];
  }
};
const usableCards = computed(() => (cards.value ?? []).filter(c => !isCardExpired(c)));
const defaultCard = computed(() =>
    usableCards.value.find(c => c.isDefault) ?? usableCards.value[0] ?? null);
const hasCard = computed(() => !!defaultCard.value);
const onCardSaved = async () => {
  addCardOpen.value = false;
  cards.value = null;
  await loadCards();
};

// ── views: saved-card (default) / element fallback ──
const view = ref('saved');
const elementHost = ref(null);
const setupPending = ref(false);
const payPending = ref(false);
const done = ref(false);
const errors = ref({});
const stripeError = ref(null);
const elementReady = ref(false);
const checkout = ref(null); // element path's intent totals
let session = null;

const teardown = () => {
  try {
    session?.paymentElement?.destroy();
  } catch {
    // already gone
  }
  session = null;
  elementReady.value = false;
  done.value = false;
  stripeError.value = null;
  errors.value = {};
  checkout.value = null;
  view.value = 'saved';
  termsAccepted.value = false;
  showAcceptedTerms.value = false;
};

// The payer always pays the FACE price — card fees are never surcharged (UK law).
const totalMinor = computed(() =>
    checkout.value?.amountMinor ?? props.obligation?.amountMinor);

// ── element fallback path ──
const useOtherMethods = async () => {
  if (blocked.value) return;
  view.value = 'element';
  if (session) return;
  setupPending.value = true;
  errors.value = {};
  stripeError.value = null;
  try {
    const res = await httpRequest(endpoints.payments.checkoutEntryFeeObligation(props.obligation.id),
        entryFeePayBody(props.obligation));
    checkout.value = res;
    await nextTick();
    if (!elementHost.value) return;
    session = await mountPaymentElement(res.clientSecret, elementHost.value,
        {stripeAccount: res.stripeAccountId});
    session.paymentElement.on('ready', () => {
      elementReady.value = true;
    });
  } catch (e) {
    errors.value = normalizeErrors(e, 'Could not start the payment. Please try again');
  } finally {
    setupPending.value = false;
  }
};

watch(open, (isOpen) => {
  if (isOpen) {
    if (cards.value == null) loadCards();
  } else {
    teardown();
  }
});

// ── pay ──
const paySaved = async () => {
  payPending.value = true;
  stripeError.value = null;
  errors.value = {};
  try {
    const res = await httpRequest(
        endpoints.payments.payEntryFeeObligationWithSavedMethod(props.obligation.id),
        entryFeePayBody(props.obligation, {userPaymentMethodId: defaultCard.value?.id ?? null}));
    if (res.succeeded) {
      done.value = true;
      emit('paid');
    } else {
      stripeError.value = res.failureMessage || 'The card was declined. Try another payment method.';
    }
  } catch (e) {
    errors.value = normalizeErrors(e, 'Could not take the payment. Please try again');
  } finally {
    payPending.value = false;
  }
};

const payElement = async () => {
  if (!session) return;
  payPending.value = true;
  stripeError.value = null;
  try {
    const {error} = await confirmPayment(session);
    if (error) {
      stripeError.value = plainStripeError(error);
      return;
    }
    done.value = true;
    emit('paid');
  } catch (e) {
    errors.value = normalizeErrors(e, 'Could not take the payment. Please try again');
  } finally {
    payPending.value = false;
  }
};
</script>

<template>
  <tl-dialog
      v-model="open"
      :title="done ? 'Payment complete' : 'Pay entry fee'"
      :max-width="440"
  >
    <template v-if="done">
      <div class="pef-done">
        <v-icon size="42" color="success">mdi-check-circle</v-icon>
        <div class="pef-done-title">You're all paid up</div>
        <div class="pef-done-sub" v-if="obligation">
          {{ money(totalMinor, obligation.currency) }} to {{ obligation.entityName }}
        </div>
      </div>
    </template>

    <template v-else>
      <p v-if="payee" class="pef-payee">{{ payee }}</p>
      <div v-if="obligation" class="pef-summary mb-3">
        <div class="pef-summary-row">
          <span>{{ obligation.entityName }}</span>
          <span class="pef-amount">{{ money(obligation.amountMinor, obligation.currency) }}</span>
        </div>
        <div v-if="obligation.teamPlayerCount > 1" class="pef-team-note">
          One entry fee for your whole team ({{ obligation.teamName }}). Either of you can pay it.
        </div>
      </div>

      <p v-if="cancellationLine && !tickAsked" class="pef-cancellation" data-testid="pef-cancellation-line">{{ cancellationLine }}</p>
      <div v-if="tickAsked" class="pef-terms">
        <div class="tl-label">Entry fee terms</div>
        <div class="pef-terms-box" v-html="termsAsHtml(obligation.termsHtml)"/>
        <p v-if="cancellationLine" class="pef-cancellation" data-testid="pef-cancellation">{{ cancellationLine }}</p>
        <v-checkbox v-model="termsAccepted" density="compact" hide-details class="mt-1">
          <template #label>
            <span class="pef-terms-tick">{{ ENTRY_FEE_PAY_TICK }}</span>
          </template>
        </v-checkbox>
      </div>
      <div v-else-if="obligation?.termsHtml" class="pef-terms" data-testid="pef-terms-accepted">
        <div class="pef-terms-accepted">
          <v-icon size="16" color="success" icon="mdi-check-circle"/>
          <span>{{ ENTRY_FEE_TERMS_ALREADY_ACCEPTED }}</span>
          <a href="#" class="pef-link" @click.prevent="showAcceptedTerms = !showAcceptedTerms">
            {{ showAcceptedTerms ? 'Hide the terms' : 'Read the terms' }}
          </a>
        </div>
        <div v-if="showAcceptedTerms" class="pef-terms-box mt-2" v-html="termsAsHtml(obligation.termsHtml)"/>
      </div>

      <!-- ── saved-card view ── -->
      <template v-if="view === 'saved'">
        <div v-if="cards == null" class="pe-loading">
          <v-progress-circular indeterminate size="22" width="2" color="primary"/>
          <span>Loading your saved cards…</span>
        </div>
        <template v-else-if="hasCard">
          <div class="pef-card-row">
            <v-icon size="18" icon="mdi-credit-card-outline"/>
            <span class="pef-card-label">{{ cardLabel(defaultCard) }}</span>
            <router-link :to="{path: '/your-profile', query: {tab: 'payments'}}" class="pef-link">Change</router-link>
          </div>
          <p class="text-caption text-medium-emphasis mt-2 mb-0">
            Charged securely via Stripe.
            <a href="#" class="pef-link" @click.prevent="useOtherMethods">Other payment methods</a>
          </p>
        </template>
        <template v-else>
          <div class="pef-card-row pef-card-row--none">
            <v-icon size="18" icon="mdi-credit-card-plus-outline" color="primary"/>
            <span class="pef-card-label">Save a card once and pay with one click, here and at
              every club on Tap Leagues.</span>
          </div>
          <p class="text-caption text-medium-emphasis mt-2 mb-0">
            Prefer Apple&nbsp;Pay, Google&nbsp;Pay or a one-off card?
            <a href="#" class="pef-link" @click.prevent="useOtherMethods">Other payment methods</a>
          </p>
        </template>
      </template>

      <!-- ── element fallback (wallets + card, nothing saved) ── -->
      <template v-else>
        <p class="text-caption text-medium-emphasis mb-3">
          Pay securely with Apple&nbsp;Pay, Google&nbsp;Pay or card. Tap Leagues never sees the number.
        </p>
        <div v-if="setupPending" class="pe-loading">
          <v-progress-circular indeterminate size="22" width="2" color="primary"/>
          <span>Preparing secure payment…</span>
        </div>
        <div ref="elementHost"/>
      </template>

      <v-alert v-if="stripeError" type="error" variant="tonal" density="compact" class="mt-3">
        {{ stripeError }}
      </v-alert>
      <crud-error-display class="mt-3" :errors="errors"/>
    </template>

    <template #actions>
      <template v-if="done">
        <v-btn color="primary" variant="flat" @click="open = false">Done</v-btn>
      </template>
      <template v-else>
        <v-btn variant="outlined" @click="open = false">Cancel</v-btn>
        <v-btn
            v-if="view === 'saved' && cards != null && !hasCard"
            color="primary"
            prepend-icon="mdi-credit-card-plus-outline"
            @click="addCardOpen = true"
        >
          Add a card
        </v-btn>
        <v-btn
            v-else-if="view === 'saved'"
            color="primary"
            prepend-icon="mdi-lock"
            :disabled="!hasCard || blocked"
            :loading="payPending"
            @click="paySaved"
        >
          Pay {{ obligation ? money(totalMinor, obligation.currency) : '' }}
        </v-btn>
        <v-btn
            v-else
            color="primary"
            prepend-icon="mdi-lock"
            :disabled="!elementReady || blocked"
            :loading="payPending"
            @click="payElement"
        >
          Pay {{ obligation ? money(totalMinor, obligation.currency) : '' }}
        </v-btn>
      </template>
    </template>
  </tl-dialog>

  <add-card-dialog v-model="addCardOpen" @saved="onCardSaved"/>
</template>

<style scoped lang="scss">
.pef-cancellation {
  font-size: 12.5px;
  line-height: 1.45;
  margin: 8px 0 4px;
}

.pef-payee {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 8px;
}

.pef-terms { margin-bottom: 12px; }
.pef-terms-box {
  max-height: 160px; overflow-y: auto; font-size: 12.5px; line-height: 1.45;
  background: rgb(var(--v-theme-light)); border-radius: 8px; padding: 10px 12px; white-space: normal;
}
.pef-terms-tick { font-size: 13px; }
.pef-terms-accepted {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-size: 13px;
}
.pe-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  font-size: 13px;
  color: rgb(var(--v-theme-charcoal));
}

.pef-summary {
  padding: 12px 14px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  font-size: 14px;
  font-weight: 500;
}

.pef-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pef-summary-sub {
  margin-top: 4px;
  font-size: 12.5px;
  font-weight: 400;
  color: rgb(var(--v-theme-textSubtle));
}

.pef-summary-total {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.pef-team-note {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgb(var(--v-theme-borderLight));
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}

.pef-amount {
  font-size: 18px;
  font-weight: 700;
}

.pef-card-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  font-size: 13.5px;
  font-weight: 500;
}

.pef-card-row--none {
  font-weight: 400;
  color: rgb(var(--v-theme-charcoal));
}

.pef-card-label {
  flex: 1;
  min-width: 0;
}

.pef-link {
  color: rgb(var(--v-theme-primary));
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
}

.pef-done {
  text-align: center;
  padding: 12px 0 4px;
}

.pef-done-title {
  font-size: 15px;
  font-weight: 700;
  margin-top: 8px;
}

.pef-done-sub {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 2px;
}
</style>
